import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { Button } from 'react-bootstrap';

import { useForm } from 'react-hook-form';
import {
  FILTER_FIELD_STYLE,
  FormProvider,
  RHFControl,
  RHFDatePicker,
  RHFSelect
} from 'components/common/form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useDonationTypes, usePackages } from 'hooks/useDonationSettings';

const formSchema = Yup.object().shape({
  donorName: Yup.string(),
  type: Yup.array().of(Yup.string()),
  donationType: Yup.array().of(Yup.string()),
  package: Yup.array().of(Yup.string()),
  paymentMethod: Yup.array().of(Yup.string()),
  donationSource: Yup.array().of(Yup.string()),
  cancellationReason: Yup.array().of(Yup.string()),
  date: Yup.object().shape({
    from: Yup.string(),
    to: Yup.string()
  })
});

const CANCELLATION_REASON_OPTIONS = [
  { label: 'Reduced financial capacity', value: 'Reduced financial capacity' },
  {
    label: 'Change in personal circumstances',
    value: 'Change in personal circumstances'
  },
  {
    label: 'Lack of communication/updates',
    value: 'Lack of communication/updates'
  },
  {
    label: "Unsure of my donation's impact",
    value: "Unsure of my donation's impact"
  },
  {
    label: 'Concern regarding fund management',
    value: 'Concern regarding fund management'
  },
  {
    label: 'Set up by mistake or forgotten donation',
    value: 'Set up by mistake or forgotten donation'
  },
  {
    label: 'Supporting a different cause/charity',
    value: 'Supporting a different cause/charity'
  },
  { label: 'Campaign goal reached', value: 'Campaign goal reached' },
  { label: 'Loss of trust', value: 'Loss of trust' },
  { label: 'Feeling unappreciated', value: 'Feeling unappreciated' },
  {
    label: 'Irrelevant projects or content',
    value: 'Irrelevant projects or content'
  }
];

const DONATION_TYPE_OPTIONS = [
  { label: 'One-off', value: 'one-off' },
  { label: 'Monthly', value: 'monthly' }
];

const DONATION_SOURCE_OPTIONS = [
  { label: 'Online', value: 'online' },
  { label: 'Phone', value: 'phone' }
];

const DEFAULT_VALUES = {
  donorName: '',
  type: [],
  donationType: [],
  package: [],
  paymentMethod: [],
  donationSource: [],
  cancellationReason: [],
  date: {
    from: '',
    to: ''
  }
};

function DonationsFilterForm({ onFilter, paymentMethodOptions, initialValues }) {
  const form = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    // initialValues: filters from the last visit to this list (see index.js)
    defaultValues: { ...DEFAULT_VALUES, ...(initialValues || {}) },
    resolver: yupResolver(formSchema)
  });

  const { donationTypes } = useDonationTypes();
  const { packages } = usePackages();

  const donationTypeOptions = useMemo(
    () =>
      donationTypes?.map(({ id, donation_type_name }) => ({
        label: donation_type_name,
        value: id
      })) || [],
    [donationTypes]
  );

  const packageOptions = useMemo(
    () =>
      packages?.map(({ id, package_title }) => ({
        label: package_title,
        value: id
      })) || [],
    [packages]
  );

  return (
    <FormProvider methods={form} onSubmit={form.handleSubmit(onFilter)}>
      <div className="d-flex align-items-end gap-2 flex-wrap">
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 160 }}>
          <RHFControl
            name="donorName"
            label="Donor name"
            placeholder="Search donor..."
            noOptional={true}
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 140 }}>
          <RHFSelect
            name="type"
            label="Donation Type"
            options={donationTypeOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 100 }}>
          <RHFSelect
            name="donationType"
            label="Type"
            options={DONATION_TYPE_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 140 }}>
          <RHFSelect
            name="package"
            label="Package"
            options={packageOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 120 }}>
          <RHFSelect
            name="paymentMethod"
            label="Payment Method"
            options={paymentMethodOptions}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 100 }}>
          <RHFSelect
            name="donationSource"
            label="Source"
            options={DONATION_SOURCE_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ ...FILTER_FIELD_STYLE, minWidth: 160 }}>
          <RHFSelect
            name="cancellationReason"
            label="Cancellation Reason"
            options={CANCELLATION_REASON_OPTIONS}
            noOptional={true}
            isClearable={true}
            isMulti
          />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <RHFDatePicker name="date.from" label="From" noOptional={true} />
        </div>
        <div style={{ flex: '0 1 110px' }}>
          <RHFDatePicker name="date.to" label="To" noOptional={true} />
        </div>
        <div className="d-flex gap-2" style={{ paddingTop: '1.5rem' }}>
          <Button
            variant="secondary"
            type="button"
            onClick={() => {
              form.reset(DEFAULT_VALUES);
              form.handleSubmit(onFilter)();
            }}
          >
            Clear
          </Button>
          <Button type="submit">Filter</Button>
        </div>
      </div>
    </FormProvider>
  );
}

DonationsFilterForm.propTypes = {
  onFilter: PropTypes.func,
  paymentMethodOptions: PropTypes.array,
  initialValues: PropTypes.object
};

export default DonationsFilterForm;
